import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import InventoryService from '@/api/inventory.service';

interface PurchaseRequisitionItem {
  id?: number;
  itemId: string;
  itemName: string;
  description: string;
  quantity: number;
  estimatedUnitPrice: number;
  justification: string;
  urgency: 'low' | 'medium' | 'high' | 'urgent';
}

interface PurchaseRequisitionFormData {
  requisitionNumber: string;
  requestDate: string;
  requiredDate: string;
  department: string;
  requestedBy: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'draft' | 'pending_approval' | 'approved' | 'rejected' | 'cancelled';
  justification: string;
  budgetCode: string;
  estimatedTotalCost: number;
  notes: string;
  items: PurchaseRequisitionItem[];
}

interface PurchaseRequisitionFormProps {
  mode: 'create' | 'edit';
  initialData?: any;
  onSubmit: (data: PurchaseRequisitionFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const PurchaseRequisitionForm: React.FC<PurchaseRequisitionFormProps> = ({
  mode,
  initialData,
  onSubmit,
  onCancel,
  isLoading = false
}) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState<PurchaseRequisitionFormData>({
    requisitionNumber: '',
    requestDate: new Date().toISOString().split('T')[0],
    requiredDate: '',
    department: '',
    requestedBy: '',
    priority: 'medium',
    status: 'draft',
    justification: '',
    budgetCode: '',
    estimatedTotalCost: 0,
    notes: '',
    items: []
  });

  const [availableItems, setAvailableItems] = useState<any[]>([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const businessId = parseInt(localStorage.getItem('businessId') || '1');

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      setFormData({
        requisitionNumber: initialData.requisitionNumber || '',
        requestDate: initialData.requestDate ? new Date(initialData.requestDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        requiredDate: initialData.requiredDate ? new Date(initialData.requiredDate).toISOString().split('T')[0] : '',
        department: initialData.department || '',
        requestedBy: initialData.requestedBy || '',
        priority: initialData.priority || 'medium',
        status: initialData.status || 'draft',
        justification: initialData.justification || '',
        budgetCode: initialData.budgetCode || '',
        estimatedTotalCost: initialData.estimatedTotalCost || 0,
        notes: initialData.notes || '',
        items: initialData.items || []
      });
    }
    fetchAvailableItems();
  }, [mode, initialData]);

  useEffect(() => {
    // Calculate total cost when items change
    const totalCost = formData.items.reduce((sum, item) => sum + (item.quantity * item.estimatedUnitPrice), 0);
    if (totalCost !== formData.estimatedTotalCost) {
      setFormData(prev => ({ ...prev, estimatedTotalCost: totalCost }));
    }
  }, [formData.items]);

  const fetchAvailableItems = async () => {
    try {
      setIsLoadingItems(true);
      const response = await InventoryService.getInventoryItems(businessId);
      if (response.data?.success) {
        setAvailableItems(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load available items",
      });
    } finally {
      setIsLoadingItems(false);
    }
  };

  const handleInputChange = (field: keyof PurchaseRequisitionFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const addItem = () => {
    const newItem: PurchaseRequisitionItem = {
      itemId: '',
      itemName: '',
      description: '',
      quantity: 1,
      estimatedUnitPrice: 0,
      justification: '',
      urgency: 'medium'
    };
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const updateItem = (index: number, field: keyof PurchaseRequisitionItem, value: any) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };

    // If item is selected, populate name and description
    if (field === 'itemId' && value) {
      const selectedItem = availableItems.find(item => item.id.toString() === value);
      if (selectedItem) {
        updatedItems[index].itemName = selectedItem.name;
        updatedItems[index].description = selectedItem.description || '';
      }
    }

    setFormData(prev => ({ ...prev, items: updatedItems }));
  };

  const removeItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.requisitionNumber.trim()) {
      newErrors.requisitionNumber = 'Requisition number is required';
    }

    if (!formData.requestDate) {
      newErrors.requestDate = 'Request date is required';
    }

    if (!formData.requiredDate) {
      newErrors.requiredDate = 'Required date is required';
    }

    if (!formData.department.trim()) {
      newErrors.department = 'Department is required';
    }

    if (!formData.requestedBy.trim()) {
      newErrors.requestedBy = 'Requested by is required';
    }

    if (!formData.justification.trim()) {
      newErrors.justification = 'Justification is required';
    }

    if (formData.items.length === 0) {
      newErrors.items = 'At least one item is required';
    }

    // Validate items
    formData.items.forEach((item, index) => {
      if (!item.itemId) {
        newErrors[`item_${index}_itemId`] = 'Item selection is required';
      }
      if (!item.quantity || item.quantity <= 0) {
        newErrors[`item_${index}_quantity`] = 'Valid quantity is required';
      }
      if (!item.estimatedUnitPrice || item.estimatedUnitPrice <= 0) {
        newErrors[`item_${index}_estimatedUnitPrice`] = 'Valid estimated unit price is required';
      }
      if (!item.justification.trim()) {
        newErrors[`item_${index}_justification`] = 'Item justification is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please fix the errors before submitting",
      });
      return;
    }

    onSubmit(formData);
  };

  const getPriorityBadge = (priority: string) => {
    const priorityConfig = {
      low: { variant: 'secondary' as const, label: 'Low' },
      medium: { variant: 'outline' as const, label: 'Medium' },
      high: { variant: 'default' as const, label: 'High' },
      urgent: { variant: 'destructive' as const, label: 'Urgent' }
    };

    const config = priorityConfig[priority as keyof typeof priorityConfig] || { variant: 'secondary' as const, label: priority };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle>Requisition Information</CardTitle>
          <CardDescription>Basic details about the purchase requisition</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="requisitionNumber">Requisition Number *</Label>
              <Input
                id="requisitionNumber"
                value={formData.requisitionNumber}
                onChange={(e) => handleInputChange('requisitionNumber', e.target.value)}
                placeholder="Enter requisition number"
                className={errors.requisitionNumber ? 'border-red-500' : ''}
              />
              {errors.requisitionNumber && (
                <p className="text-sm text-red-500">{errors.requisitionNumber}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="requestDate">Request Date *</Label>
              <Input
                id="requestDate"
                type="date"
                value={formData.requestDate}
                onChange={(e) => handleInputChange('requestDate', e.target.value)}
                className={errors.requestDate ? 'border-red-500' : ''}
              />
              {errors.requestDate && (
                <p className="text-sm text-red-500">{errors.requestDate}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="requiredDate">Required Date *</Label>
              <Input
                id="requiredDate"
                type="date"
                value={formData.requiredDate}
                onChange={(e) => handleInputChange('requiredDate', e.target.value)}
                className={errors.requiredDate ? 'border-red-500' : ''}
              />
              {errors.requiredDate && (
                <p className="text-sm text-red-500">{errors.requiredDate}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="department">Department *</Label>
              <Input
                id="department"
                value={formData.department}
                onChange={(e) => handleInputChange('department', e.target.value)}
                placeholder="Enter department"
                className={errors.department ? 'border-red-500' : ''}
              />
              {errors.department && (
                <p className="text-sm text-red-500">{errors.department}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="requestedBy">Requested By *</Label>
              <Input
                id="requestedBy"
                value={formData.requestedBy}
                onChange={(e) => handleInputChange('requestedBy', e.target.value)}
                placeholder="Enter requester name"
                className={errors.requestedBy ? 'border-red-500' : ''}
              />
              {errors.requestedBy && (
                <p className="text-sm text-red-500">{errors.requestedBy}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select value={formData.priority} onValueChange={(value) => handleInputChange('priority', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="budgetCode">Budget Code</Label>
              <Input
                id="budgetCode"
                value={formData.budgetCode}
                onChange={(e) => handleInputChange('budgetCode', e.target.value)}
                placeholder="Enter budget code"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="pending_approval">Pending Approval</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="justification">Justification *</Label>
            <Textarea
              id="justification"
              value={formData.justification}
              onChange={(e) => handleInputChange('justification', e.target.value)}
              placeholder="Provide justification for this requisition"
              rows={3}
              className={errors.justification ? 'border-red-500' : ''}
            />
            {errors.justification && (
              <p className="text-sm text-red-500">{errors.justification}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Additional Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Any additional notes or comments"
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Items Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Requisition Items</CardTitle>
              <CardDescription>Items requested in this requisition</CardDescription>
            </div>
            <Button
              type="button"
              onClick={addItem}
              className="flex items-center gap-2"
              disabled={isLoadingItems}
            >
              <Plus className="h-4 w-4" />
              Add Item
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {errors.items && (
            <p className="text-sm text-red-500 mb-4">{errors.items}</p>
          )}
          
          {formData.items.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No items added yet. Click "Add Item" to get started.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Est. Unit Price</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Urgency</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Select
                          value={item.itemId}
                          onValueChange={(value) => updateItem(index, 'itemId', value)}
                        >
                          <SelectTrigger className={errors[`item_${index}_itemId`] ? 'border-red-500' : ''}>
                            <SelectValue placeholder="Select item" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableItems.map((availableItem) => (
                              <SelectItem key={availableItem.id} value={availableItem.id.toString()}>
                                {availableItem.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors[`item_${index}_itemId`] && (
                          <p className="text-xs text-red-500 mt-1">{errors[`item_${index}_itemId`]}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <Input
                          value={item.description}
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                          placeholder="Item description"
                          className="min-w-[150px]"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                          placeholder="Qty"
                          min="0"
                          step="0.01"
                          className={`min-w-[80px] ${errors[`item_${index}_quantity`] ? 'border-red-500' : ''}`}
                        />
                        {errors[`item_${index}_quantity`] && (
                          <p className="text-xs text-red-500 mt-1">{errors[`item_${index}_quantity`]}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={item.estimatedUnitPrice}
                          onChange={(e) => updateItem(index, 'estimatedUnitPrice', parseFloat(e.target.value) || 0)}
                          placeholder="Price"
                          min="0"
                          step="0.01"
                          className={`min-w-[100px] ${errors[`item_${index}_estimatedUnitPrice`] ? 'border-red-500' : ''}`}
                        />
                        {errors[`item_${index}_estimatedUnitPrice`] && (
                          <p className="text-xs text-red-500 mt-1">{errors[`item_${index}_estimatedUnitPrice`]}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">
                          {formatCurrency(item.quantity * item.estimatedUnitPrice)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={item.urgency}
                          onValueChange={(value) => updateItem(index, 'urgency', value)}
                        >
                          <SelectTrigger className="min-w-[100px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="urgent">Urgent</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Item Justifications */}
              <div className="space-y-4">
                <h4 className="font-medium">Item Justifications</h4>
                {formData.items.map((item, index) => (
                  <div key={index} className="space-y-2">
                    <Label htmlFor={`justification_${index}`}>
                      Justification for {item.itemName || `Item ${index + 1}`} *
                    </Label>
                    <Textarea
                      id={`justification_${index}`}
                      value={item.justification}
                      onChange={(e) => updateItem(index, 'justification', e.target.value)}
                      placeholder="Provide justification for this item"
                      rows={2}
                      className={errors[`item_${index}_justification`] ? 'border-red-500' : ''}
                    />
                    {errors[`item_${index}_justification`] && (
                      <p className="text-sm text-red-500">{errors[`item_${index}_justification`]}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Total Summary */}
          {formData.items.length > 0 && (
            <div className="mt-6 pt-4 border-t">
              <div className="flex justify-between items-center">
                <span className="text-lg font-medium">Estimated Total Cost:</span>
                <span className="text-xl font-bold">{formatCurrency(formData.estimatedTotalCost)}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Actions */}
      <div className="flex items-center justify-end gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isLoading}
          className="flex items-center gap-2"
        >
          {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          {mode === 'create' ? 'Create Requisition' : 'Update Requisition'}
        </Button>
      </div>
    </form>
  );
};

export default PurchaseRequisitionForm; 